import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { ANNOUNCEMENTS, CATEGORIES, CLINIC, PRODUCTS } from "../data/catalog";
import { EASE } from "./Reveal";

const SHOP_LINKS = [
  { label: "All Products", cat: "All" },
  { label: "Hair Care", cat: "Hair Care" },
  { label: "Skin Care", cat: "Skin Care" },
  { label: "Body Care", cat: "Body Care" },
  { label: "Sets & Gifting", cat: "Sets & Gifting" },
];

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/our-story", label: "Our Story" },
  { to: "/how-we-make-it", label: "How We Make It" },
  { to: "/the-clinic", label: "The Clinic" },
  { to: "/contact", label: "Contact" },
];

const AnnouncementBar = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ANNOUNCEMENTS.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="bg-ink-deep px-4 py-2 text-center" data-testid="announcement-bar">
      <AnimatePresence mode="wait">
        <motion.p
          key={i}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-cream/85"
        >
          {ANNOUNCEMENTS[i]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const SearchOverlay = ({ onClose }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const results = q.trim()
    ? PRODUCTS.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()))
    : [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[80] bg-cream/97 backdrop-blur-sm"
      data-testid="search-overlay"
    >
      <div className="mx-auto max-w-3xl px-4 pt-24 md:pt-32">
        <div className="flex items-center justify-between">
          <p className="eyebrow">Search the range</p>
          <button onClick={onClose} data-testid="search-close" aria-label="Close search" className="flex h-10 w-10 items-center justify-center rounded-full border hairline">
            <X className="h-5 w-5" />
          </button>
        </div>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Hair oil, lip balm, face cream…"
          data-testid="search-input"
          className="mt-6 w-full border-b-2 border-ink/20 bg-transparent pb-4 font-serif text-3xl font-light italic placeholder:text-ink/25 md:text-4xl"
        />
        <div className="mt-8">
          {q.trim() && results.length === 0 && (
            <p className="text-sm text-ink/60" data-testid="search-no-results">
              Nothing matched that. There are only ten products here —{" "}
              <Link to="/shop" onClick={onClose} className="underline underline-offset-4">they all are</Link>.
            </p>
          )}
          {results.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                onClose();
                navigate(`/product/${p.slug}`);
              }}
              data-testid={`search-result-${p.slug}`}
              className="flex w-full items-center justify-between border-b hairline py-4 text-left transition-colors hover:bg-white/60"
            >
              <span className="font-serif text-xl">{p.name}</span>
              <span className="text-sm text-ink/50">{p.size} · ₹{p.price}</span>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count } = useCart();
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50">
        <AnnouncementBar />
        <header className="border-b hairline bg-cream/88 backdrop-blur-md">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-8">
            <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
              <span className="font-serif text-[21px] font-medium tracking-[0.08em]">AYUSHI</span>
              <span className="hidden font-serif italic text-sm text-ink/60 sm:inline">by Dr. Shibina</span>
            </Link>

            <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
              {LINKS.slice(0, 1).map((l) => (
                <NavLink key={l.to} to={l.to} data-testid="nav-link-home" className={({ isActive }) => `text-[12px] uppercase tracking-[0.18em] transition-colors ${isActive ? "text-ink" : "text-ink/55 hover:text-ink"}`}>
                  {l.label}
                </NavLink>
              ))}
              <div className="group relative">
                <NavLink to="/shop" data-testid="nav-link-shop" className={({ isActive }) => `inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.18em] transition-colors ${isActive ? "text-ink" : "text-ink/55 hover:text-ink"}`}>
                  Shop <ChevronDown className="h-3.5 w-3.5" />
                </NavLink>
                <div className="invisible absolute left-1/2 top-full z-50 w-52 -translate-x-1/2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                  <div className="border hairline bg-cream py-2">
                    {SHOP_LINKS.map((s) => (
                      <Link
                        key={s.cat}
                        to={s.cat === "All" ? "/shop" : `/shop?cat=${encodeURIComponent(s.cat)}`}
                        data-testid={`nav-shop-${s.label.toLowerCase().replace(/\s|&/g, "-")}`}
                        className="block px-5 py-2.5 text-[12px] uppercase tracking-[0.16em] text-ink/65 transition-colors hover:bg-cream-2 hover:text-ink"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              {LINKS.slice(1).map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={({ isActive }) => `text-[12px] uppercase tracking-[0.18em] transition-colors ${isActive ? "text-ink" : "text-ink/55 hover:text-ink"}`}>
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen(true)} data-testid="nav-search-button" aria-label="Search" className="hidden h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-cream-2 sm:flex">
                <Search className="h-[17px] w-[17px]" />
              </button>
              <Link to="/account" data-testid="nav-account-button" aria-label="Account" className="hidden h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-cream-2 sm:flex">
                <User className="h-[17px] w-[17px]" />
              </Link>
              <Link to="/cart" data-testid="nav-cart-button" aria-label="Cart" className="relative flex h-10 w-10 items-center justify-center rounded-full border hairline transition-colors duration-300 hover:bg-ink hover:text-cream">
                <ShoppingBag className="h-[16px] w-[16px]" />
                {count > 0 && (
                  <span data-testid="nav-cart-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-sage-deep px-1 text-[10px] font-semibold text-cream">
                    {count}
                  </span>
                )}
              </Link>
              <Link to="/shop" data-testid="nav-shop-now" className="hidden rounded-full bg-ink px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-cream transition-colors hover:bg-ink-deep md:inline-block">
                Shop Now
              </Link>
              <button onClick={() => setOpen(true)} data-testid="nav-menu-button" aria-label="Open menu" className="flex h-10 w-10 items-center justify-center rounded-full border hairline lg:hidden">
                <Menu className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
        </header>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-ink-deep text-cream"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <img src="/assets/logo-white.png" alt="Ayushi by Dr. Shibina" className="h-10 w-auto" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/25">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8 py-6">
              {[{ to: "/", label: "Home" }, { to: "/shop", label: "Shop" }, ...LINKS.slice(1)].map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}>
                  <Link to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="block border-b border-cream/10 py-4 font-serif text-3xl font-light">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex gap-6 pt-6 text-[12px] uppercase tracking-[0.2em] text-cream/70">
                <Link to="/account" data-testid="mobile-nav-account">{user ? "My Account" : "Sign In"}</Link>
                <Link to="/cart" data-testid="mobile-nav-cart">Cart {count > 0 ? `(${count})` : ""}</Link>
              </motion.div>
            </nav>
            <div className="px-8 pb-10 text-sm text-cream/55">
              <p>{CLINIC.hours}</p>
              <p className="mt-1">WhatsApp {CLINIC.phone}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>{searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}</AnimatePresence>
    </>
  );
};

export default Nav;
